import { Alert } from '@mui/material';
import { useFormik } from 'formik';
import { useContext } from 'react';
import { array, object, ObjectSchema, string } from 'yup';
import QuestionsApi from '../../../../../../apis/QuestionsApi';
import FormikCheckboxGroup from '../../../../../../components/FormikCheckboxGroup/FormikCheckboxGroup';
import SnackbarContext from '../../../../../../contexts/SnackbarContext';
import useApi from '../../../../../../lib/useApi';
import {
  SelectManyAnswerWithDis,
  SelectManyQuestionWithDis,
} from '../../../../../../types/QuestionAndAnswersWithDiscriminator';
import QuestionCardFooter from './QuestionCardFooter';

/**
 * The properties for the select many question
 */
interface SelectManyQuestionProps extends BaseQuestionProps {
  question: SelectManyQuestionWithDis;
}

/**
 * The select many question component, to be displayed within a card within the quiz module
 */
const SelectManyQuestion = (props: SelectManyQuestionProps) => {
  const questionsApi = useApi(QuestionsApi);
  const schema: ObjectSchema<SelectManyAnswerWithDis> = object({
    $type: string().required(),
    answers: array().min(1).required(),
  });
  const snackbar = useContext(SnackbarContext);

  const formik = useFormik<SelectManyAnswerWithDis>({
    initialValues: {
      $type: props.question.$type,
      answers: [],
    },
    validationSchema: schema,
    onSubmit: (val, { setSubmitting, resetForm }) => {
      questionsApi
        .AnswerQuestion(props.question.questionId, val)
        .then(props.handlePostAnswer)
        .catch(() => {
          snackbar.setAlert(
            <Alert variant="filled" severity="error">
              An error occurred while submitting the answer. Please try again.
              If this continues, contact us.
            </Alert>
          );
          snackbar.triggerSnackbar();
        })
        .finally(() => {
          setSubmitting(false);
          resetForm();
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikCheckboxGroup
        dataTestId="select-many-question-id"
        formik={formik}
        name="answers"
        options={props.question.possibleAnswers.reduce(
          (acc: Record<string, string>, cur: string) => {
            acc[cur] = cur;
            return acc;
          },
          {} as Record<string, string>
        )}
      />
      <QuestionCardFooter
        isLoading={props.isLoading || formik.isSubmitting}
        selectedAnswer={formik.values.answers.length > 0}
        completedQuestions={props.completedQuestions}
        questionCountMinimum={props.questionCountMinimum}
      />
    </form>
  );
};

export default SelectManyQuestion;
